// src/pages/reports/RewardsPointsBook/RewardsTable.tsx
import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  ListItemText,
  MenuItem,
  Select,
  Checkbox,
  TableSortLabel,
  Typography,
  Button,
  ListSubheader,
  FormControl,
  Tooltip,
} from "@mui/material";
import { exportRewardsData, rewardPoint } from "../api";
import { getToken } from "../../../utils/utils";
import PaginationReports from "../../brands/PaginationReports";
import { useDebounce } from "use-debounce";
import Export from "../../../assests/images/export.svg";
import Sorting from "../../../assests/images/Sorting.svg";
import * as XLSX from "xlsx";
import { RewardsBookRequest } from "./types";

// simple custom arrow (replicates DropDownFilter style)
const CustomArrow: React.FC<{ open: boolean; onClick: () => void }> = ({ open, onClick }) => (
  <span
    onClick={(e) => {
      e.stopPropagation(); // prevent Select double-firing
      onClick();
    }}
    style={{
      display: "inline-block",
      width: 11,
      right: "10px",
      height: "8px",
      borderRight: "2px solid #000",
      borderBottom: "2px solid #000",
      transform: open ? "rotate(-135deg)" : "rotate(45deg)",
      transition: "transform 0.2s ease",
      marginLeft: "20px",
      position: "relative",
      cursor: "pointer",
      pointerEvents: "auto",
    }}
  />
);

export interface RewardPointResponse {
  region?: string;
  asm_name?: string;
  area_code?: string;
  tse_name?: string;
  territory_code?: string;
  outlet_salesforce_id?: string;
  sfa_id?: string;
  utm_id: number;
  account_id: number;
  fullName: string;
  member_since: string;
  created_at?: string;
  updated_at?: string;
  outlet_name: string;
  outlet_category: string;
  location: string;
  points_expires_in?: string;
  total_points: number;
  userId?: number;
  phone_no: string;
  Current_points: number | string;
  pmi_entity_code: string;
  Tasks_given: number | string;
  Total_lifetime_points: number | string;
  Total_redeemed_points: number | string;
  Last_redemption_date: string | null;
  Tasks_completed: number | string;
  Current_active_tasks: number | string;
  Current_active_tasks_completed: number | string;
  last_login: string | null;
  contact_id: string;
}

interface RewardsTableProps {
  startDate: Date | null;
  endDate: Date | null;
  searchQuery: string;
  sortOption: "highest" | "lowest" | null;
  selectedColumns?: string[];
  appliedFilters?:
    | {
        locations?: string[] | null;
        outletTypes?: string[] | null;
        outletNames?: string[] | null;
        lastloginStart?: string | null;
        lastloginEnd?: string | null;

        // Member-since (camelCase)
        memberSinceStart?: string | null;
        memberSinceEnd?: string | null;

        // Member-since (snake_case)
        member_since_start?: string | null;
        member_since_end?: string | null;

        // NOTE: backend PMI key (snake_case)
        pmi_entity_code?: string[] | null;

        [k: string]: any;
      }
    | null;
}

interface Filters {
  [key: string]: { [key: string]: boolean };
}

/* ---------------------------
   Date helpers + lists
--------------------------- */
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const isValidDate = (d: any) => d instanceof Date && !isNaN(d.getTime());

const formatDisplayDate = (value: any): string => {
  if (!value || value === "-") return "-";
  const d = new Date(value);
  if (!isValidDate(d)) return String(value ?? "-");
  const dd = String(d.getDate()).padStart(2, "0");
  const mmm = MONTHS[d.getMonth()];
  const yyyy = d.getFullYear();
  return `${dd}-${mmm}-${yyyy}`;
};

// column labels (UI) that should display as dates
const DATE_COLS = new Set<string>([
  "Member Since",
  "Last Login Date",
  "Last Redemption Date",
]);

// data keys (TS type keys) that should sort as dates
const DATE_KEYS = new Set<keyof RewardPointResponse>([
  "member_since",
  "last_login",
  "Last_redemption_date" as keyof RewardPointResponse,
]);

const ExpandableTableRow = ({ row, visibleColumns, columnKeyMap }: any) => {
  return (
    <TableRow>
      {visibleColumns.map((col: string, index: number) => {
        const rawVal = row[columnKeyMap[col] as keyof RewardPointResponse];
        const cellText = DATE_COLS.has(col) ? formatDisplayDate(rawVal) : String(rawVal ?? "-");

        return (
          <TableCell
            key={col}
            component={index === 0 ? "th" : undefined}
            scope={index === 0 ? "row" : undefined}
            sx={{
              // force single line with ellipsis everywhere
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 240, // tweak as needed
            }}
            title={cellText}
          >
            {cellText}
          </TableCell>
        );
      })}
    </TableRow>
  );
};

const RewardsTable: React.FC<RewardsTableProps> = ({
  startDate,
  endDate,
  searchQuery,
  sortOption,
  selectedColumns,
  appliedFilters,
}) => {
  const [users, setUsers] = useState<RewardPointResponse[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedtransactions, setselectedtransactions] = useState<string[]>([]);
  const [selectedredemptions, setselectedredemptions] = useState<string[]>([]);
  const [colSelectOpen, setColSelectOpen] = useState(false);

  // ✅ track PMI selected from popup (BACKEND KEY)
  const [selectedPmiEntities, setSelectedPmiEntities] = useState<string[]>([]);

  // ⭐ NEW retailer-level selections
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedAsmNames, setSelectedAsmNames] = useState<string[]>([]);
  const [selectedAreaCodes, setSelectedAreaCodes] = useState<string[]>([]);
  const [selectedTseNames, setSelectedTseNames] = useState<string[]>([]);
  const [selectedTerritoryCodes, setSelectedTerritoryCodes] = useState<string[]>([]);
  const [selectedOutletSfIds, setSelectedOutletSfIds] = useState<string[]>([]);
  const [selectedSfaIds, setSelectedSfaIds] = useState<string[]>([]);

  const fetchAbortRef = useRef<AbortController | null>(null);

  const [page, setPage] = useState(1);
  const [contactidmain, setcontactidmain] = useState<string[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedOutletTypes, setSelectedOutletTypes] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedlastlogin, setSelectedlastlogin] = useState<string[]>([]);
  const [selectedOutletNames, setSelectedOutletNames] = useState<string[]>([]);
  const [memberSinceRange, setMemberSinceRange] = useState<[Date | null, Date | null]>([null, null]);
  const [orderBy, setOrderBy] = useState<keyof RewardPointResponse>("account_id");
  const [order, setOrder] = useState<"asc" | "desc">("asc");

  const token = getToken();
  const [filters, setFilters] = useState<Filters>({
    locations: { all: true },
    pointsTransaction: { all: true },
    redemptionRequest: { all: true },
  });

  const [debouncedSearchTerm] = useDebounce(searchQuery, 500);
  const itemsPerPage = 10;

  const allColumns = [
    "User ID",
    "User Name",
    "Outlet Name",
    "Outlet Category",
    "Location",
    "Last Login Date",
    "Member Since",
    "Total Points",
    "Phone Number",
    "Current Points",
    "PMI Entity Code",
    "Tasks Given",
    "Total Lifetime Points",
    "Total Redeemed Points",
    "Last Redemption Date",
    "Tasks Completed",
    "Current Active Tasks",
    "Current Active Tasks Completed",

    // ⭐ NEW columns (not in default visible set)
    "Region",
    "ASM Name",
    "Area Code",
    "TSE Name",
    "Territory Code",
    "Outlet Salesforce ID",
    "SFA ID",
  ];

  const columnKeyMap: Record<string, keyof RewardPointResponse | string> = {
    "Region": "region",
    "ASM Name": "asm_name",
    "Area Code": "area_code",
    "TSE Name": "tse_name",
    "Territory Code": "territory_code",
    "Outlet Salesforce ID": "outlet_salesforce_id",
    "SFA ID": "sfa_id",
    "User ID": "account_id",
    "User Name": "fullName",
    "Outlet Name": "outlet_name",
    "Outlet Category": "outlet_category",
    "Location": "location",
    "Member Since": "member_since",
    "Total Points": "total_points",
    "Phone Number": "phone_no",
    "Current Points": "Current_points",
    "PMI Entity Code": "pmi_entity_code",
    "Tasks Given": "Tasks_given",
    "Total Lifetime Points": "Total_lifetime_points",
    "Total Redeemed Points": "Total_redeemed_points",
    "Last Redemption Date": "Last_redemption_date",
    "Tasks Completed": "Tasks_completed",
    "Current Active Tasks": "Current_active_tasks",
    "Current Active Tasks Completed": "Current_active_tasks_completed",
    "Last Login Date": "last_login",
  };

  // map used only for export — backend expects snake_case canonical keys
  const exportKeyMap: Record<string, string> = {
    "Region": "region",
    "ASM Name": "asm_name",
    "Area Code": "area_code",
    "TSE Name": "tse_name",
    "Territory Code": "territory_code",
    "Outlet Salesforce ID": "outlet_salesforce_id",
    "SFA ID": "sfa_id",
    "User ID": "account_id",
    "User Name": "full_name",
    "Outlet Name": "outlet_name",
    "Outlet Category": "outlet_category",
    "Location": "location",
    "Member Since": "member_since",
    "Total Points": "total_points",
    "Phone Number": "phone_no",
    "Current Points": "Current_points",
    "PMI Entity Code": "pmi_entity_code",
    "Tasks Given": "Tasks_given",
    "Total Lifetime Points": "Total_lifetime_points",
    "Total Redeemed Points": "Total_redeemed_points",
    "Last Redemption Date": "Last_redemption_date",
    "Tasks Completed": "Tasks_completed",
    "Current Active Tasks": "Current_active_tasks",
    "Current Active Tasks Completed": "Current_active_tasks_completed",
    "Last Login Date": "last_login",
  };

  /* ---------------------------
     DEFAULT COLUMNS (mirror RewardsTrTable)
     --------------------------- */
  const DEFAULT_VISIBLE = allColumns.slice(0, 6);
  const DEFAULT_SET = new Set<string>(DEFAULT_VISIBLE);
  const withDefaultsIfEmpty = (arr: string[]) => (arr && arr.length ? arr : [...DEFAULT_VISIBLE]);

  const [visibleColumns, setVisibleColumns] = useState<string[]>(DEFAULT_VISIBLE);
  const [tempSelectedColumns, setTempSelectedColumns] = useState<string[]>(DEFAULT_VISIBLE);

  const [colSearch, setColSearch] = useState<string>("");
  const colSearchRef = useRef<HTMLInputElement | null>(null);

  const filteredColumns = React.useMemo(() => {
    const q = (colSearch || "").trim().toLowerCase();
    if (!q) return allColumns;
    return allColumns.filter((c) => c.toLowerCase().includes(q));
  }, [allColumns, colSearch]);

  const allFilteredSelected =
    filteredColumns.length > 0 && filteredColumns.every((c) => tempSelectedColumns.includes(c));

  // 🔁 Match RewardsTrTable behavior for select-all, preserving defaults
  const toggleSelectAllOnFiltered = () => {
    if (allFilteredSelected) {
      // Deselect only non-defaults in the filtered set
      setTempSelectedColumns((prev) => {
        const next = prev.filter((p) => !filteredColumns.includes(p) || DEFAULT_SET.has(p));
        // ensure defaults stay present
        DEFAULT_VISIBLE.forEach((d) => {
          if (!next.includes(d)) next.push(d);
        });
        return next;
      });
    } else {
      // Select all filtered + keep defaults selected
      setTempSelectedColumns((prev) =>
        Array.from(new Set<string>([...prev, ...filteredColumns, ...DEFAULT_VISIBLE]))
      );
    }
  };

  // Sync appliedFilters coming from parent into local state
  useEffect(() => {
    if (appliedFilters) {
      if (Array.isArray(appliedFilters.locations)) {
        setSelectedLocations(appliedFilters.locations);
      }
      if (Array.isArray(appliedFilters.outletTypes)) {
        setSelectedOutletTypes(appliedFilters.outletTypes);
      }
      if (Array.isArray(appliedFilters.outletNames)) {
        setSelectedOutletNames(appliedFilters.outletNames);
      }
      // ✅ sync PMI (backend key)
      if (Array.isArray(appliedFilters.pmi_entity_code)) {
        setSelectedPmiEntities(appliedFilters.pmi_entity_code);
      }

      // ⭐ NEW retailer-level fields
      setSelectedRegions(appliedFilters.region ?? []);
      setSelectedAsmNames(appliedFilters.asm_name ?? []);
      setSelectedAreaCodes(appliedFilters.area_code ?? []);
      setSelectedTseNames(appliedFilters.tse_name ?? []);
      setSelectedTerritoryCodes(appliedFilters.territory_code ?? []);
      setSelectedOutletSfIds(appliedFilters.outlet_salesforce_id ?? []);
      setSelectedSfaIds(appliedFilters.sfa_id ?? []);

      if (appliedFilters.lastloginStart || appliedFilters.lastloginEnd) {
        const start = appliedFilters.lastloginStart ? new Date(appliedFilters.lastloginStart) : null;
        const end = appliedFilters.lastloginEnd ? new Date(appliedFilters.lastloginEnd) : null;
        setDateRange([start, end]);
      }

      const memStartStr =
        appliedFilters.memberSinceStart ?? (appliedFilters as any).member_since_start ?? null;
      const memEndStr =
        appliedFilters.memberSinceEnd ?? (appliedFilters as any).member_since_end ?? null;
      const memStart = memStartStr ? new Date(memStartStr) : null;
      const memEnd = memEndStr ? new Date(memEndStr) : null;
      setMemberSinceRange([memStart, memEnd]);

      setPage(1);
    } else {
      setSelectedLocations([]);
      setSelectedOutletTypes([]);
      setSelectedOutletNames([]);
      setSelectedPmiEntities([]); // ✅ reset PMI too

      // ⭐ reset retailer-level selections
      setSelectedRegions([]);
      setSelectedAsmNames([]);
      setSelectedAreaCodes([]);
      setSelectedTseNames([]);
      setSelectedTerritoryCodes([]);
      setSelectedOutletSfIds([]);
      setSelectedSfaIds([]);

      setDateRange([null, null]);
      setMemberSinceRange([null, null]);
      setPage(1);
    }
  }, [appliedFilters]);

  const formatDate = (date: Date | string | null | undefined): string => {
    if (!date) return "";
    const parsedDate = new Date(date);
    return (
      parsedDate.getFullYear() +
      "-" +
      String(parsedDate.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(parsedDate.getDate()).padStart(2, "0")
    );
  };

  const fetchRewardPointApi = async () => {
    // abort previous request
    if (fetchAbortRef.current) {
      try {
        fetchAbortRef.current.abort();
      } catch (e) {}
    }
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    try {
      const start = formatDate(startDate);
      const end = formatDate(endDate);

      const sort = sortOption
        ? {
            field: "total_points",
            direction: sortOption === "highest" ? "desc" : "asc",
          }
        : null;

      const reqBody: RewardsBookRequest = {
        page,
        limit: itemsPerPage,
        filters: {
          location: selectedLocations.length > 0 ? selectedLocations : null,
          outletTypes: selectedOutletTypes.length > 0 ? selectedOutletTypes : null,
          lastloginStart: dateRange[0] ? formatDate(dateRange[0]) : null,
          lastloginEnd: dateRange[1] ? formatDate(dateRange[1]) : null,
          memberSinceStart: memberSinceRange[0] ? formatDate(memberSinceRange[0]) : null,
          memberSinceEnd: memberSinceRange[1] ? formatDate(memberSinceRange[1]) : null,
          outletNames: selectedOutletNames.length > 0 ? selectedOutletNames : null,
          // ✅ include PMI with backend key
          pmi_entity_code: selectedPmiEntities.length > 0 ? selectedPmiEntities : null,

          // ⭐ NEW retailer-level filters
          region: selectedRegions.length > 0 ? selectedRegions : null,
          asm_name: selectedAsmNames.length > 0 ? selectedAsmNames : null,
          area_code: selectedAreaCodes.length > 0 ? selectedAreaCodes : null,
          tse_name: selectedTseNames.length > 0 ? selectedTseNames : null,
          territory_code: selectedTerritoryCodes.length > 0 ? selectedTerritoryCodes : null,
          outlet_salesforce_id: selectedOutletSfIds.length > 0 ? selectedOutletSfIds : null,
          sfa_id: selectedSfaIds.length > 0 ? selectedSfaIds : null,
        } as any, // minimal: allow extra keys if type is stricter
        search_key: debouncedSearchTerm || null,
        start: start || null,
        end: end || null,
        sort: sortOption,
        type: undefined,
      };

      const res = await rewardPoint(reqBody, token, controller.signal);

      const mappedData = res?.data?.map((item: any) => ({
        region: item.region ?? "-",
        asm_name: item.asm_name ?? "-",
        area_code: item.area_code ?? "-",
        tse_name: item.tse_name ?? "-",
        territory_code: item.territory_code ?? "-",
        outlet_salesforce_id: item.outlet_salesforce_id ?? "-",
        sfa_id: item.sfa_id ?? "-",
        utm_id: item.utm_id,
        account_id: item.account_id,
        fullName: item.fullName,
        member_since: item.member_since || "-",
        created_at: item.created_at || "-",
        updated_at: item.updated_at || "-",
        outlet_name: item.outlet_name,
        outlet_category: item.outlet_category,
        location: item.location,
        points_expires_in: item.points_expires_in || "-",
        total_points: item.total_points ?? 0,
        userId: item.userId ?? 0,
        phone_no: item.phone_no || "-",
        Current_points: item.Current_points ?? "-",
        pmi_entity_code: item.pmi_entity_code || "-",
        Tasks_given: item.Tasks_given ?? "-",
        Total_lifetime_points: item.Total_lifetime_points ?? "-",
        Total_redeemed_points: item.Total_redeemed_points ?? "-",
        Last_redemption_date: item.Last_redemption_date || "-",
        Tasks_completed: item.Tasks_completed ?? "-",
        Current_active_tasks: item.Current_active_tasks ?? "-",
        Current_active_tasks_completed: item.Current_active_tasks_completed ?? "-",
        last_login: item.last_login || "-",
        contact_id: item.contact_id || "",
      }));

      setcontactidmain(res?.data?.map((item: any) => item.contact_id) || []);
      setUsers(mappedData || []);

      // ✅ be tolerant to BE returning either totalCount or count
      const totalItems = (res?.totalCount ?? res?.count ?? 0) as number;
      setTotalRecords(totalItems);
      setTotalPages(Math.ceil(totalItems / itemsPerPage));
    } catch (error: any) {
      if (error?.name === "AbortError") {
        console.log("fetchRewardPointApi aborted");
        return;
      }
      console.error("Error fetching reward points:", error);
      setUsers([]);
      setTotalRecords(0);
      setTotalPages(1);
    } finally {
      if (fetchAbortRef.current === controller) fetchAbortRef.current = null;
    }
  };

  // fetch on deps
  useEffect(() => {
    fetchRewardPointApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    startDate,
    endDate,
    selectedtransactions,
    selectedLocations,
    selectedOutletTypes,
    selectedPmiEntities, // ✅ react when PMI changes
    // ⭐ NEW deps to refetch when retailer-level filters change
    selectedRegions,
    selectedAsmNames,
    selectedAreaCodes,
    selectedTseNames,
    selectedTerritoryCodes,
    selectedOutletSfIds,
    selectedSfaIds,

    dateRange,
    memberSinceRange,
    debouncedSearchTerm,
    page,
    sortOption,
    visibleColumns,
  ]);

  // cleanup on unmount
  useEffect(() => {
    return () => {
      if (fetchAbortRef.current) {
        try {
          fetchAbortRef.current.abort();
        } catch (e) {}
        fetchAbortRef.current = null;
      }
    };
  }, []);
  // keep this with other useEffects
  useEffect(() => {
    // whenever the header DatePicker changes, mirror it locally so the API payload reflects it
    setDateRange([startDate, endDate]);
    setPage(1); // optional: jump back to first page on date change
  }, [startDate, endDate]);

  const handleExportClick = async () => {
    try {
      // use exportKeyMap to send snake_case keys backend expects
      const selectedColumnKeys = visibleColumns
        .map((label) => exportKeyMap[label] ?? (columnKeyMap[label] as string))
        .filter(Boolean) as string[];

      // ✅ Build the SAME shape as list API: send a `filters` object
      const payload = {
        filters: {
          location: selectedLocations.length > 0 ? selectedLocations : null,
          outletTypes: selectedOutletTypes.length > 0 ? selectedOutletTypes : null,
          outletNames: selectedOutletNames.length > 0 ? selectedOutletNames : null,
          pmi_entity_code: selectedPmiEntities.length > 0 ? selectedPmiEntities : null,
          lastloginStart: dateRange[0] ? formatDate(dateRange[0]) : null,
          lastloginEnd: dateRange[1] ? formatDate(dateRange[1]) : null,
          memberSinceStart: memberSinceRange[0] ? formatDate(memberSinceRange[0]) : null,
          memberSinceEnd: memberSinceRange[1] ? formatDate(memberSinceRange[1]) : null,

          // ⭐ NEW retailer-level filters
          region: selectedRegions.length > 0 ? selectedRegions : null,
          asm_name: selectedAsmNames.length > 0 ? selectedAsmNames : null,
          area_code: selectedAreaCodes.length > 0 ? selectedAreaCodes : null,
          tse_name: selectedTseNames.length > 0 ? selectedTseNames : null,
          territory_code: selectedTerritoryCodes.length > 0 ? selectedTerritoryCodes : null,
          outlet_salesforce_id: selectedOutletSfIds.length > 0 ? selectedOutletSfIds : null,
          sfa_id: selectedSfaIds.length > 0 ? selectedSfaIds : null,
        },
        search_key: debouncedSearchTerm || null,
        start: startDate ? formatDate(startDate) : null,
        end: endDate ? formatDate(endDate) : null,
        sort: sortOption || null,
        // columns for backend to include
        columns: selectedColumnKeys.join(","),
        // keep for compatibility — we'll normalize inside exportRewardsData
        selectedColumns: selectedColumnKeys,
      };

      const blob = await exportRewardsData(payload, token);

      const data = await blob.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const csv = XLSX.utils.sheet_to_csv(workbook.Sheets[workbook.SheetNames[0]]);
      const csvBlob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(csvBlob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "rewards-export.csv");
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting CSV:", error);
    }
  };

  const handleSort = (column: keyof RewardPointResponse) => {
    const isAsc = orderBy === column && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(column);
  };

  // put these helpers above `sortedUsers`
  const NUMERIC_COLS = new Set<keyof RewardPointResponse | string>([
    "account_id",
    "utm_id",
    "total_points",
    "Current_points",
    "Tasks_given",
    "Total_lifetime_points",
    "Total_redeemed_points",
    "Tasks_completed",
    "Current_active_tasks",
    "Current_active_tasks_completed",
    "userId",
  ]);

  const toNumber = (v: unknown) => {
    if (typeof v === "number") return v;
    if (typeof v === "string") {
      const n = parseFloat(v.replace(/,/g, "")); // tolerate "1,234"
      return Number.isFinite(n) ? n : NaN;
    }
    return NaN;
  };

  const toStringCI = (v: unknown) => (v ?? "").toString().toLowerCase();

  const toTime = (v: unknown) => {
    const d = new Date(String(v));
    return isNaN(d.getTime()) ? NaN : d.getTime();
  };

  const sortedUsers = [...users].sort((a, b) => {
    const aVal = (a as any)[orderBy];
    const bVal = (b as any)[orderBy];

    // handle null/undefined consistently
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return order === "asc" ? -1 : 1;
    if (bVal == null) return order === "asc" ? 1 : -1;

    // date compare for date columns
    if (DATE_KEYS.has(orderBy as keyof RewardPointResponse)) {
      const at = toTime(aVal);
      const bt = toTime(bVal);
      if (Number.isFinite(at) && Number.isFinite(bt)) {
        if (at < bt) return order === "asc" ? -1 : 1;
        if (at > bt) return order === "asc" ? 1 : -1;
        return 0;
      }
    }

    // numeric compare for numeric columns
    if (NUMERIC_COLS.has(orderBy)) {
      const an = toNumber(aVal);
      const bn = toNumber(bVal);
      if (Number.isFinite(an) && Number.isFinite(bn)) {
        if (an < bn) return order === "asc" ? -1 : 1;
        if (an > bn) return order === "asc" ? 1 : -1;
        return 0;
      }
    }

    // fallback: case-insensitive string compare
    const as = toStringCI(aVal);
    const bs = toStringCI(bVal);
    if (as < bs) return order === "asc" ? -1 : 1;
    if (as > bs) return order === "asc" ? 1 : -1;
    return 0;
  });

  return (
    <Box sx={{ background: "#fff", border: "1px solid #d1cfcf", borderRadius: "10px" }}>
      <Box sx={{ padding: "25px", display: "flex", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: "16px", fontWeight: "600", marginTop: "10px" }}>
          Total {totalRecords} Users
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {/* --- Column picker with tooltip --- */}
          <Box sx={{ marginRight: "20px" }}>
            <Tooltip
              title={tempSelectedColumns.length ? tempSelectedColumns.join(", ") : "Select Columns"}
              placement="top"
              arrow
              PopperProps={{
                modifiers: [
                  {
                    name: "offset",
                    options: { offset: [0, 10] },
                  },
                ],
              }}
            >
              {/* Tooltip must have exactly one child element */}
              <FormControl sx={{ minWidth: 240, width: 240 }}>
                <Select
                  multiple
                  displayEmpty
                  value={tempSelectedColumns}
                  open={colSelectOpen}
                  onOpen={() => setColSelectOpen(true)}
                  onClose={() => {
                    setColSelectOpen(false);
                    const next = withDefaultsIfEmpty(tempSelectedColumns);
                    setTempSelectedColumns(next);   // keep internal state valid
                    setVisibleColumns(next);        // commit to table
                    setColSearch("");
                  }}
                  onChange={(e) => {
                    const val = e.target.value;
                    const arr = typeof val === "string" ? val.split(",") : (val as string[]);
                    if (Array.isArray(arr) && (arr as any[]).includes("__ALL__")) {
                      toggleSelectAllOnFiltered();
                      return;
                    }
                    const next = withDefaultsIfEmpty(Array.isArray(arr) ? arr : []);
                    setTempSelectedColumns(next);
                  }}
                  renderValue={(selected) => {
                    if ((selected as string[]).length === 0) return "Select Columns";
                    return (selected as string[]).join(", ");
                  }}
                  inputProps={{ "aria-label": "columns-select" }}
                  IconComponent={() => (
                    <CustomArrow open={colSelectOpen} onClick={() => setColSelectOpen((v) => !v)} />
                  )}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        minWidth: 240,
                        paddingTop: 0,
                        paddingBottom: 0,
                        overflow: "overlay",
                        borderRadius: 10,
                        background: "#fff",
                        boxShadow: "none",
                      },
                    },
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                    disableAutoFocusItem: true,
                    MenuListProps: {
                      style: { paddingTop: 0, paddingBottom: 0, maxHeight: "250px", overflowY: "auto" },
                      onKeyDown: (e: React.KeyboardEvent) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          e.stopPropagation();
                          (e.nativeEvent as KeyboardEvent).stopImmediatePropagation?.();
                          setTimeout(() => colSearchRef.current?.focus(), 0);
                        }
                      },
                    },
                  }}
                  sx={{
                    minWidth: 240,
                    height: "40px",
                    borderRadius: "10px",
                    backgroundColor: "#fff",
                    marginRight: "8px",
                    width: "240px",
                    position: "relative",
                    "& .MuiSelect-icon": { display: "none" },
                    "&:hover, &.Mui-focused, &.Mui-focusVisible": {
                      background: "transparent !important",
                      boxShadow: "none !important",
                      outline: "none !important",
                    },
                  }}
                >
                  <ListSubheader
                    sx={{
                      bgcolor: "white",
                      p: 1,
                      display: "flex",
                      alignItems: "center",
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        px: 1,
                        py: 0.5,
                        border: "1px solid #ccc",
                        borderRadius: 1,
                        bgcolor: "transparent",
                      }}
                      onClick={(ev) => ev.stopPropagation()}
                    >
                      <input
                        ref={colSearchRef}
                        value={colSearch}
                        onChange={(e) => setColSearch(e.target.value)}
                        placeholder="Search columns..."
                        style={{
                          width: "100%",
                          border: "none",
                          outline: "none",
                          fontSize: 14,
                          background: "transparent",
                          padding: "4px",
                          height: "40px",
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          (e.nativeEvent as KeyboardEvent).stopImmediatePropagation?.();
                        }}
                      />
                      {colSearch && (
                        <Box
                          component="span"
                          sx={{
                            cursor: "pointer",
                            fontWeight: "bold",
                            fontSize: 18,
                            lineHeight: 1,
                            px: 1,
                          }}
                          onClick={(ev) => {
                            ev.stopPropagation();
                            setColSearch("");
                            setTimeout(() => colSearchRef.current?.focus(), 0);
                          }}
                        >
                          ×
                        </Box>
                      )}
                    </Box>
                  </ListSubheader>

                  <MenuItem
                    dense
                    value="__ALL__"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      toggleSelectAllOnFiltered();
                    }}
                  >
                    <Checkbox checked={allFilteredSelected} />
                    <ListItemText primary="Select All" />
                  </MenuItem>

                  {filteredColumns.length ? (
                    filteredColumns.map((name) => (
                      <MenuItem key={name} value={name}>
                        <Checkbox checked={tempSelectedColumns.includes(name)} />
                        <ListItemText primary={name} />
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled dense>
                      <ListItemText primary="No results" />
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Tooltip>
          </Box>

          <Button
            variant="contained"
            sx={{
              textTransform: "capitalize",
              backgroundColor: "#fff",
              fontSize: "14px",
              fontWeight: "500",
              position: "relative",
              left: "-7px",
              height: "40px",
              padding: "16px",
              borderRadius: "10px",
              color: "#C8172F",
              border: "1px solid #C8172F",
              boxShadow: "none",
              "& .MuiButton-endIcon": {
                fontSize: "30px",
                color: "var(--Icon-Colour, #C8172F)",
              },
              "&:before, &:after": { border: "none" },
            }}
            startIcon={<img src={Export} alt="export" />}
            onClick={() => handleExportClick()}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        sx={{ borderTop: "1px solid #d3d3d3", boxShadow: "none", maxWidth: "100%", overflowX: "auto" }}
      >
        <Table sx={{ minWidth: visibleColumns.length * 150, tableLayout: "fixed" }}>
          <TableHead sx={{ background: "#EEF4FF" }}>
            <TableRow>
              {visibleColumns.map((col) => (
                <TableCell
                  key={col}
                  sx={{
                    minWidth: 150,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 300,
                  }}
                >
                  <TableSortLabel
                    active={orderBy === columnKeyMap[col]}
                    direction={orderBy === columnKeyMap[col] ? order : "asc"}
                    onClick={() => {
                      const colKey = columnKeyMap[col] as keyof RewardPointResponse;
                      handleSort(colKey);
                    }}
                    IconComponent={() => <img src={Sorting} alt="sort" style={{ width: 16, height: 16 }} />}
                  >
                    {col}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length > 0 ? (
              sortedUsers.map((row) => (
                <ExpandableTableRow
                  key={row.account_id}
                  row={row}
                  visibleColumns={visibleColumns}
                  columnKeyMap={columnKeyMap}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} align="center">
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {users.length > 0 && (
        <PaginationReports page={page} limit={itemsPerPage} totalPages={totalPages} setPage={setPage} />
      )}
    </Box>
  );
};

export default RewardsTable;
